import { zodResolver } from '@hookform/resolvers/zod'
import { FEEDBACK_TYPES, type FeedbackType, feedbackSubmitSchema } from '@sahab/contracts'
import { useMutation } from '@tanstack/react-query'
import { useEffect, useMemo, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import type { z } from 'zod'
import { api } from '../../lib/api'
import { errorMessage } from '../../lib/errors'
import { useLanguage } from '../../lib/i18n'
import { translateMessage, zodErrorMap } from '../../lib/validation'
import { Button } from '../../ui/button'
import { Dialog, DialogContent } from '../../ui/dialog'
import { Alert } from '../../ui/feedback'
import { Field } from '../../ui/field'
import { Input, Textarea } from '../../ui/input'
import { Select } from '../../ui/select'
import { toast } from '../../ui/toaster'

type FormInput = z.input<typeof feedbackSubmitSchema>
type FormOutput = z.output<typeof feedbackSubmitSchema>

const EMPTY: FormInput = { type: 'GENERAL', subject: '', message: '' }

/** "Send feedback" from the user menu: a bug, an idea or a complaint, to the Sahab team. */
export function FeedbackDialog({
  open,
  onOpenChange,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const { t } = useTranslation('platform')
  const { t: tc } = useTranslation()
  const { language } = useLanguage()
  const [formError, setFormError] = useState<string | null>(null)
  const send = useMutation({ mutationFn: (body: FormOutput) => api.post<void>('/feedback', body) })
  const resolver = useMemo(() => zodResolver(feedbackSubmitSchema, { error: zodErrorMap(tc) }), [tc])
  const {
    register,
    handleSubmit,
    reset,
    control,
    formState: { errors, isSubmitting },
  } = useForm<FormInput, unknown, FormOutput>({ resolver, defaultValues: EMPTY })

  useEffect(() => {
    if (open) {
      reset(EMPTY)
      setFormError(null)
    }
  }, [open, reset])

  const submit = handleSubmit(async (values) => {
    setFormError(null)
    try {
      await send.mutateAsync(values)
      toast.success(t('feedbackForm.sent'), { description: t('feedbackForm.sentBody') })
      onOpenChange(false)
    } catch (error) {
      setFormError(errorMessage(tc, language, error))
    }
  })

  return (
    <Dialog open={open} onOpenChange={(next) => (isSubmitting ? undefined : onOpenChange(next))}>
      <DialogContent
        title={t('feedbackForm.title')}
        description={t('feedbackForm.body')}
        className="max-w-lg"
        hideClose={isSubmitting}
        footer={
          <>
            <Button disabled={isSubmitting} onClick={() => onOpenChange(false)}>
              {tc('actions.cancel')}
            </Button>
            <Button type="submit" form="feedback-send-form" variant="primary" loading={isSubmitting}>
              {t('feedbackForm.submit')}
            </Button>
          </>
        }
      >
        <form id="feedback-send-form" noValidate onSubmit={submit} className="flex flex-col gap-4">
          {formError ? <Alert tone="danger">{formError}</Alert> : null}
          <Field label={t('feedbackForm.type')}>
            <Controller
              control={control}
              name="type"
              render={({ field }) => (
                <Select<FeedbackType>
                  value={field.value}
                  onValueChange={field.onChange}
                  options={FEEDBACK_TYPES.map((type) => ({
                    value: type,
                    label: t(`feedback.types.${type}`),
                  }))}
                />
              )}
            />
          </Field>
          <Field
            label={t('feedbackForm.subject')}
            required
            error={translateMessage(tc, errors.subject?.message)}
          >
            <Input autoFocus {...register('subject')} />
          </Field>
          <Field
            label={t('feedbackForm.message')}
            required
            error={translateMessage(tc, errors.message?.message)}
          >
            <Textarea rows={6} {...register('message')} />
          </Field>
        </form>
      </DialogContent>
    </Dialog>
  )
}
